/**
 * /fullguide — visual product guide and FAQ.
 *
 * The screenshots are static support assets in public/support-guide. Callouts are
 * drawn as SVG overlays so the source images remain unchanged and scale with the
 * figure on narrow screens.
 */
import {
  BookOpen,
  Check,
  ChevronRight,
  Copy,
  Disc3,
  Gift,
  HelpCircle,
  Image as ImageIcon,
  Library,
  LifeBuoy,
  Music2,
  Search,
  Settings,
  Shuffle,
  Wand2,
} from 'lucide-react';
import type { ReactNode, UIEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo, SectionHeading } from '../components/ui';
import { FAQ_TOPICS } from '../data/faq';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import { paths } from './paths';

const HEADER_TRANSITION_GUARD_MS = 240;
const SUBNAV_JUMP_GUARD_MS = 400;
const SEARCH_TRANSITION_MS = 180;

interface Callout {
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  arrow?: {
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
  };
}

interface GuideSection {
  id: string;
  title: string;
  summary: string;
  icon: ReactNode;
  image: string;
  imageAlt: string;
  callouts: Callout[];
  steps: string[];
}

interface GuideQuestion {
  question: string;
  answer: string;
}

const guideSections: GuideSection[] = [
  {
    id: 'home',
    title: 'Find Your Way Around',
    summary: 'Start from Home to create music, explore discoverable tracks, or jump into the main areas from the left navigation.',
    icon: <BookOpen size={17} color={c.accent} />,
    image: '/support-guide/01-home.png',
    imageAlt: 'Lyrixsa AI home screen with navigation, create button, explore button, and featured music.',
    callouts: [
      { label: 'Create a song', x: 31, y: 29, w: 12, h: 5.2, arrow: { fromX: 25, fromY: 22, toX: 36, toY: 31 } },
      { label: 'Explore music', x: 43, y: 29, w: 12, h: 5.2, arrow: { fromX: 52, fromY: 24, toX: 48, toY: 31 } },
      { label: 'Main navigation', x: 1, y: 7.5, w: 13, h: 27, arrow: { fromX: 19, fromY: 15, toX: 12, toY: 13 } },
    ],
    steps: [
      'Use the left navigation to move between Create, Library, Studio, Publisher, billing, settings, and help.',
      'Click Create a Song when you want to generate a new track from an idea.',
      'Use Explore Music and the search bar to discover public tracks by title, genre, mood, or artist.',
    ],
  },
  {
    id: 'text-music',
    title: 'Create With Text Music',
    summary: 'Write anything from a short idea to a complete, highly structured brief: lyrics, explicit content where permitted, arrangement instructions, vocal direction, and production detail are all valid creative input.',
    icon: <Music2 size={17} color={c.accent} />,
    image: '/support-guide/02-text-music.png',
    imageAlt: 'Text Music create screen showing the prompt box and Generate Music button.',
    callouts: [
      { label: 'Choose a creation method', x: 31, y: 11.8, w: 51, h: 11, arrow: { fromX: 20, fromY: 14, toX: 35, toY: 15 } },
      { label: 'Describe the song', x: 31, y: 27.5, w: 51, h: 20, arrow: { fromX: 26, fromY: 36, toX: 40, toY: 32 } },
      { label: 'Generate', x: 32.5, y: 48, w: 11, h: 5.2, arrow: { fromX: 25, fromY: 55, toX: 38, toY: 50 } },
    ],
    steps: [
      'Open Create, then Text Music.',
      'Write anything you want the song to express, from a short concept to extended lyrics and a detailed arrangement brief.',
      'Open the selector row to set genre, voice, and language, then press Generate Music.',
    ],
  },
  {
    id: 'dna',
    title: 'Use Music DNA',
    summary: 'Paste a Spotify track link so Lyrixsa can analyze the feel and turn it into an original prompt.',
    icon: <Wand2 size={17} color={c.accent} />,
    image: '/support-guide/03-music-dna.png',
    imageAlt: 'Music DNA screen with Spotify song link field and Analyze button.',
    callouts: [
      { label: 'Paste Spotify link', x: 32, y: 13, w: 41, h: 4.8, arrow: { fromX: 26, fromY: 21, toX: 41, toY: 15.5 } },
      { label: 'Analyze', x: 74, y: 13, w: 8, h: 4.8, arrow: { fromX: 87, fromY: 19, toX: 78, toY: 15.5 } },
      { label: 'DNA results appear here', x: 31, y: 20.8, w: 51, h: 11, arrow: { fromX: 45, fromY: 37, toX: 56, toY: 27 } },
    ],
    steps: [
      'Copy a Spotify track URL and paste it into the Music DNA field.',
      'Click Analyze to inspect genre, mood, energy, BPM, and musical character.',
      'Use the generated DNA as inspiration for a new, original song prompt.',
    ],
  },
  {
    id: 'mixer-start',
    title: 'Start a Genre Mix',
    summary: 'The mixer begins with genre selection and shows the workflow before you create.',
    icon: <Shuffle size={17} color={c.accent} />,
    image: '/support-guide/04-genre-mixer-start.png',
    imageAlt: 'AI Genre Mixer start screen with genre selectors, Mix button, Premium badge, and Guide button.',
    callouts: [
      { label: 'Genre slots', x: 31, y: 10, w: 51, h: 20, arrow: { fromX: 24, fromY: 17, toX: 43, toY: 17 } },
      { label: 'Mix button', x: 76, y: 34, w: 5.5, h: 4.8, arrow: { fromX: 89, fromY: 36, toX: 78.5, toY: 36 } },
      { label: 'Workflow guide', x: 31, y: 45, w: 48, h: 6, arrow: { fromX: 24, fromY: 52, toX: 47, toY: 47 } },
    ],
    steps: [
      'Open Genre Mixer from the Create menu.',
      'Pick at least two genre slots before mixing.',
      'Use the Guide button when you want a quick reminder of the full mixer flow.',
    ],
  },
  {
    id: 'mixer',
    title: 'Blend Genres',
    summary: 'Pick at least two genres, review compatibility, tune the creative controls, then create.',
    icon: <Shuffle size={17} color={c.accent} />,
    image: '/support-guide/05-genre-mixer-controls.png',
    imageAlt: 'Genre Mixer controls showing selected genres, compatibility, mood, tempo, language, vocals, song length, and energy.',
    callouts: [
      { label: 'Select genres', x: 32, y: 13.5, w: 50, h: 12, arrow: { fromX: 24, fromY: 17, toX: 39, toY: 16 } },
      { label: 'Compatibility score', x: 32, y: 29, w: 17, h: 8, arrow: { fromX: 24, fromY: 35, toX: 37, toY: 34 } },
      { label: 'Creative controls', x: 31, y: 45, w: 51, h: 19, arrow: { fromX: 26, fromY: 52, toX: 51, toY: 49 } },
    ],
    steps: [
      'Choose Genre 1 and Genre 2. Genre 3 is optional.',
      'Check the compatibility score to understand how naturally the blend fits.',
      'Set mood, tempo, language, vocal type, length, and energy before generating.',
    ],
  },
  {
    id: 'mixer-create',
    title: 'Edit the Mixer Prompt',
    summary: 'Use the AI prompt as a starting point, add extra direction, and create the final song.',
    icon: <Shuffle size={17} color={c.accent} />,
    image: '/support-guide/06-genre-mixer-create.png',
    imageAlt: 'Genre Mixer generated prompt section with Copy Prompt, Regenerate Prompt, Save Prompt, variations, and Create button.',
    callouts: [
      { label: 'Editable AI prompt', x: 31, y: 20, w: 51, h: 10, arrow: { fromX: 24, fromY: 23, toX: 45, toY: 24 } },
      { label: 'Refine or save', x: 31, y: 31.5, w: 31, h: 4.2, arrow: { fromX: 27, fromY: 39, toX: 42, toY: 34 } },
      { label: 'Create song', x: 32, y: 73, w: 49, h: 5.2, arrow: { fromX: 24, fromY: 70, toX: 56, toY: 75.5 } },
    ],
    steps: [
      'Read the generated prompt and edit anything that should change.',
      'Use Copy Prompt, Regenerate Prompt, or Save Prompt when you want to reuse the idea.',
      'Add a short extra instruction if needed, choose a variation, then click Create.',
    ],
  },
  {
    id: 'image-music',
    title: 'Turn an Image Into Music',
    summary: 'Upload a visual reference and describe the mood or sound you want Lyrixsa to create.',
    icon: <ImageIcon size={17} color={c.accent} />,
    image: '/support-guide/07-image-music.png',
    imageAlt: 'Image Music screen with upload dropzone, optional description, style selector, and Generate Music button.',
    callouts: [
      { label: 'Upload image', x: 31, y: 12, w: 22, h: 35, arrow: { fromX: 22, fromY: 21, toX: 42, toY: 28 } },
      { label: 'Optional description', x: 31, y: 49, w: 51, h: 12, arrow: { fromX: 25, fromY: 57, toX: 41, toY: 52 } },
      { label: 'Generate after upload', x: 32.5, y: 63, w: 11, h: 5, arrow: { fromX: 25, fromY: 69, toX: 38, toY: 65 } },
    ],
    steps: [
      'Open Image Music and click the upload area, or drag a PNG, JPG, WEBP, or GIF into it.',
      'Add an optional description if the picture needs a specific mood, scene, or genre.',
      'Choose genre, voice, and language, then generate once a reference image is added.',
    ],
  },
  {
    id: 'library',
    title: 'Manage Songs in Library',
    summary: 'Search, filter, play, favorite, open in Studio, download, duplicate, rename, or delete tracks.',
    icon: <Library size={17} color={c.accent} />,
    image: '/support-guide/08-library-actions.png',
    imageAlt: 'Library screen with a track row and opened action menu.',
    callouts: [
      { label: 'Search and filters', x: 31, y: 14.5, w: 51, h: 4.5, arrow: { fromX: 24, fromY: 14, toX: 46, toY: 16 } },
      { label: 'Play or favorite', x: 75, y: 22, w: 7, h: 4.8, arrow: { fromX: 89, fromY: 21, toX: 78, toY: 24 } },
      { label: 'More actions', x: 70, y: 26, w: 11, h: 29, arrow: { fromX: 86, fromY: 39, toX: 74, toY: 29 } },
    ],
    steps: [
      'Use Main Library for tracks and Playlists for grouped songs.',
      'Open the three-dot menu to rename, add to a playlist, duplicate, download, or delete.',
      'Choose Open in Studio when you want to edit, remix, extend, master, or export a track.',
    ],
  },
  {
    id: 'studio',
    title: 'Edit in Lyrixsa Studio',
    summary: 'Studio gives you timeline playback, lyrics, sharing, downloads, and AI editing tools for the active song. You can open a song quickly from Library or create a dedicated Studio project for deeper work.',
    icon: <Disc3 size={17} color={c.accent} />,
    image: '/support-guide/09-studio.png',
    imageAlt: 'Lyrixsa Studio workspace with active song, AI generation tools, timeline, lyrics, and transport bar.',
    callouts: [
      { label: 'AI tools', x: 16, y: 40, w: 11, h: 50, arrow: { fromX: 31, fromY: 48, toX: 20, toY: 48 } },
      { label: 'Timeline', x: 28, y: 42.5, w: 51, h: 10, arrow: { fromX: 42, fromY: 35, toX: 54, toY: 47 } },
      { label: 'Lyrics panel', x: 80, y: 39, w: 17, h: 44, arrow: { fromX: 72, fromY: 34, toX: 86, toY: 46 } },
      { label: 'Playback bar', x: 16, y: 91, w: 81, h: 7, arrow: { fromX: 31, fromY: 85, toX: 43, toY: 94 } },
    ],
    steps: [
      'For a quick edit, open Library, use the three-dot menu on a song card, and choose Open in Studio.',
      'For heavier work, open Studio and create a new project, then choose the song or material you want to work on. Every created project can be revisited from Studio later.',
      'Use Library-to-Studio when you are testing the waters or making a small change; dedicated projects are usually easier to revisit and provide a better experience for longer editing sessions.',
      'Use AI Generation tools such as Regenerate, Extend, Remix, Replace, Split Stems, or Cover Art. Regeneration creates a whole-song alternative and makes it the new editable HEAD version.',
      'Use the bottom transport to play, scrub, adjust volume, change speed, or go fullscreen.',
    ],
  },
  {
    id: 'vault-bonus',
    title: 'Use the Producer Vault',
    summary: 'Browse producer resources and bring packs straight into your create workflow.',
    icon: <Gift size={17} color={c.accent} />,
    image: '/support-guide/10-vault.png',
    imageAlt: 'DFY Producer Vault with pack counts, featured collection, search, filters, and Open buttons.',
    callouts: [
      { label: 'Browse categories', x: 31, y: 9, w: 25, h: 25, arrow: { fromX: 22, fromY: 15, toX: 40, toY: 10 } },
      { label: 'Open collection', x: 32, y: 38, w: 11, h: 4.7, arrow: { fromX: 24, fromY: 41, toX: 38, toY: 40 } },
      { label: 'Search packs', x: 31, y: 64, w: 51, h: 4.5, arrow: { fromX: 24, fromY: 67, toX: 45, toY: 66 } },
    ],
    steps: [
      'Use the Vault tabs to review favorites, downloads, and recently used packs.',
      'Open a collection to bring prompts, presets, structures, hooks, or metadata into your workflow.',
      'Search and filter by pack type when you already know what you need.',
    ],
  },
  {
    id: 'bonus',
    title: 'Claim Bonus Packs',
    summary: 'Use the Bonus page to claim bundled extras included with your plan.',
    icon: <Gift size={17} color={c.accent} />,
    image: '/support-guide/14-bonus.png',
    imageAlt: 'Lyrixsa Bonus page with three bundled extras and Claim buttons.',
    callouts: [
      { label: 'Included extras', x: 31, y: 9, w: 51, h: 26, arrow: { fromX: 24, fromY: 18, toX: 48, toY: 13 } },
      { label: 'Claim buttons', x: 75, y: 11, w: 6.5, h: 21, arrow: { fromX: 89, fromY: 17, toX: 78, toY: 18 } },
    ],
    steps: [
      'Open Lyrixsa Bonus from Settings & Resources.',
      'Click Claim beside any bundle you want to add to your account.',
      'After claiming, open the related pack from its destination page.',
      'When you add a sample pack or voice pack to a creation, the pack takes priority over genre and vocal choices in the prompt box and over any conflicting genre or vocal instructions in your written prompt.',
    ],
  },
  {
    id: 'settings-support',
    title: 'Settings and Support',
    summary: 'Update account preferences, export defaults, password, and reach support when you need help.',
    icon: <Settings size={17} color={c.accent} />,
    image: '/support-guide/11-settings.png',
    imageAlt: 'Settings screen with account details, billing, commercial license, preferences, password, and delete account rows.',
    callouts: [
      { label: 'Edit profile', x: 77, y: 15, w: 5.5, h: 4.5, arrow: { fromX: 88, fromY: 12, toX: 79, toY: 17 } },
      { label: 'Billing and license', x: 31, y: 24, w: 51, h: 14, arrow: { fromX: 24, fromY: 32, toX: 44, toY: 28 } },
      { label: 'Preferences', x: 31, y: 51, w: 51, h: 18, arrow: { fromX: 24, fromY: 58, toX: 75, toY: 53 } },
      { label: 'Security', x: 31, y: 73, w: 51, h: 9, arrow: { fromX: 24, fromY: 78, toX: 48, toY: 75 } },
    ],
    steps: [
      'Use Settings to edit your name, profile image, plan, commercial license, and preferences.',
      'Set appearance, default vocal style, export format, and email update preference.',
      'Use Support Center to search help topics or open a support ticket.',
    ],
  },
  {
    id: 'support-center',
    title: 'Search Support or Contact Us',
    summary: 'Use Support Center when you need quick answers or a support ticket.',
    icon: <LifeBuoy size={17} color={c.accent} />,
    image: '/support-guide/12-support-center.png',
    imageAlt: 'Support Center with search, topics, and an open ticket button.',
    callouts: [
      { label: 'Search help', x: 31, y: 9, w: 51, h: 4.5, arrow: { fromX: 24, fromY: 11, toX: 45, toY: 11 } },
      { label: 'Browse topics', x: 31, y: 18, w: 51, h: 20, arrow: { fromX: 24, fromY: 26, toX: 56, toY: 23 } },
      { label: 'Open a ticket', x: 68, y: 42, w: 13, h: 4.8, arrow: { fromX: 56, fromY: 47, toX: 72, toY: 44 } },
    ],
    steps: [
      'Search by keyword first, such as billing, export, password, or credits.',
      'Open a topic card to browse related help articles.',
      'Use Open a ticket when you still need direct help.',
    ],
  },
  {
    id: 'guide-page',
    title: 'Use the Built-In Guide',
    summary: 'The in-app guide keeps short reference answers close by while users work.',
    icon: <BookOpen size={17} color={c.accent} />,
    image: '/support-guide/13-guide.png',
    imageAlt: 'Lyrixsa AI Guide accordion with getting started, prompt writing, credits, and exporting sections.',
    callouts: [
      { label: 'Open first topic', x: 31, y: 9, w: 51, h: 11, arrow: { fromX: 24, fromY: 15, toX: 52, toY: 13 } },
      { label: 'More guide topics', x: 31, y: 21, w: 51, h: 16, arrow: { fromX: 24, fromY: 31, toX: 54, toY: 28 } },
    ],
    steps: [
      'Open Lyrixsa AI Guide from Settings & Resources.',
      'Click a topic row to expand or collapse it.',
      'Use it as a quick reference for prompts, credits, and exports.',
    ],
  },
];

const studioDocSections: GuideSection[] = [
  {
    id: 'stem-splitting',
    title: 'Stem Splitting',
    summary: 'Separate a supported song into useful parts such as vocals, drums, bass, and other musical layers. Each returned stem can be previewed, and the complete stem set can be downloaded as a ZIP file.',
    icon: <Disc3 size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Open a song in Studio and choose Stem Splitting from the AI tools.', 'Wait for processing to finish, then preview the separated stems individually to check the result.', 'Download the complete set as a ZIP file when you are ready to use the stems in another tool or workflow.'],
  },
  {
    id: 'audio-inpainting',
    title: 'Audio Inpainting',
    summary: 'Audio Inpainting covers Replace, Remix, and Extend. Because precise editing of an existing recording is not yet available, Lyrixsa references the original audio and recreates a new artifact from it.',
    icon: <Wand2 size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Open the song in Studio and select Audio Inpainting.', 'Mark the portion that needs attention and describe the intended replacement clearly.', 'Preview the returned result, then keep or save the version you want.'],
  },
  {
    id: 'cover-art',
    title: 'Cover Art',
    summary: 'Generate artwork for the active song from its musical context and your visual direction.',
    icon: <ImageIcon size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Open Cover Art for the active song.', 'Describe the visual direction, subject, mood, palette, and composition you want.', 'Generate and review the image, then apply or save the result.'],
  },
  {
    id: 'audio-editing',
    title: 'Audio Editing',
    summary: 'Before editing, Lyrixsa automatically parses the loaded audio and analyzes its default data. Once that preparation is complete, you can use Studio’s direct tools for practical timing and mix adjustments without asking the generator to recreate the song.',
    icon: <Settings size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Load the audio and wait while Lyrixsa parses it and analyzes its default data for editing.', 'Select an editing action such as trim, cut, split, crossfade, fade, silence, normalize, mix, or master.', 'Set the requested range or parameters, preview the change, and apply it when ready; direct edits are kept separate from AI regeneration.'],
  },
  {
    id: 'preview-mode',
    title: 'Preview Mode',
    summary: 'Mix opens Preview Mode so you can hear the result of your mix without saving or applying it. Switch between the mix preview and the loaded audio seamlessly while you compare them.',
    icon: <BookOpen size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Split the song into stems, open the Mix tool, and set the balance you want between the available parts.', 'Click Mix to trigger Preview Mode and listen to the mixed result without saving or applying it.', 'Switch between the mix preview and the loaded audio to compare them. The Balance section remains unavailable until stems have been split.'],
  },
  {
    id: 'uploads',
    title: 'Uploads',
    summary: 'Bring supported audio or artwork into Studio so you can work from material you own or are authorized to use.',
    icon: <Library size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Choose Uploads in Studio and select a supported local file.', 'Wait for the upload and validation steps to finish.', 'Open the uploaded asset or use it in the Studio action that accepts it.'],
  },
  {
    id: 'versions-history',
    title: 'Versions History',
    summary: 'Versions History keeps Studio work non-destructive. Editing creates a new edited song/version instead of altering the existing song, and every version can be saved as a new song in Library.',
    icon: <Library size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Open Versions History for the active song to see the original and every later edited version.', 'Compare and preview versions, then load the version you want to continue editing. A newly created edit becomes the HEAD automatically; explicitly loading another version changes the HEAD.', 'Save any version as a new song in Library, or delete versions you no longer need.'],
  },
  {
    id: 'publisher-release',
    title: 'Publish a Release',
    summary: 'Prepare a Single, EP, or Album for delivery from one guided workflow. Choose music from Library, complete the release information, provide cover art and optional lyrics, select outlets, then review everything before submitting.',
    icon: <Disc3 size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Open Publisher and choose New Release, then select Single, EP, or Album.', 'Choose the tracks from your Lyrixsa Library in the intended order. Lyrixsa automatically fills in the available title, artist, genre, language, release date, and explicit-content details from your selected music; review them and edit any field if you want different release information.', 'Choose or upload release artwork, add lyrics when needed, select the stores and streaming services, then use Review to correct any incomplete information before submitting.'],
  },
  {
    id: 'publisher-dashboard',
    title: 'Manage Releases',
    summary: 'Use the Publisher dashboard to resume drafts, follow releases through review and delivery, inspect issues, and revisit the most recent Publisher screen when you return.',
    icon: <Library size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Use Overview to scan published songs, live releases, work in progress, items needing attention, and drafts.', 'Open a release to inspect its information and outlet-level status, or resume a draft to continue from the last completed step.', 'Use Activity to filter milestones, platform updates, earnings events, and issues by time range.'],
  },
  {
    id: 'publisher-reporting',
    title: 'Analytics and Earnings',
    summary: 'Review the reporting returned for your releases, including available streaming activity, audience and territory information, earnings records, deductions, and payout status.',
    icon: <BookOpen size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Open Analytics and choose all releases or one release, then select the reporting period you want to inspect.', 'Compare the available stream, audience, playlist, and geography information; an unavailable metric means no supported report has been returned for it.', 'Open Financial to review reported earnings, applicable deductions, net amounts, and payout status, then confirm your payout details in Settings.'],
  },
  {
    id: 'publisher-credits-settings',
    title: 'Publisher Settings',
    summary: 'Profile and Settings hold the identity, payout, and release defaults used by the Publisher.',
    icon: <Settings size={17} color={c.accent} />,
    image: '', imageAlt: '', callouts: [],
    steps: ['Review your Publisher profile, payout method, and distribution preferences before submitting important release work.', 'Confirm that your artist identity and release defaults are current.', 'Review each release before submission even when defaults have filled its fields.'],
  },
];

const GUIDE_QUESTIONS: Record<string, GuideQuestion[]> = {
  home: [
    { question: 'What should I do first?', answer: 'Start in Create to make a track, or open Library if you already have songs. Your navigation and account area remain available from every main workspace.' },
    { question: 'Where are my generated songs?', answer: 'Completed generations appear in Library. Open a song there to play it, organize it, download it, or continue editing it in Studio.' },
    { question: 'When does a play count?', answer: 'A play is counted after at least 30 seconds of listening in one uninterrupted playback run. Starting a track, repeatedly seeking, or listening for less than 30 seconds does not add a play.' },
    { question: 'How do Likes work?', answer: 'Like is the Love action. It is a per-account toggle: you can add or remove your Love, while the total Loves shown for a public song represents Loves from the wider audience plus your current Love.' },
    { question: 'What does Save mean?', answer: 'Save means adding a song to a playlist. The save total is the number of playlists that contain the song, so adding the same song to two playlists contributes two saves.' },
    { question: 'When is a share counted?', answer: 'A share is counted when you successfully send the Lyrixsa link through the system share sheet or copy it to the clipboard. Opening a shared link is not counted by the current app.' },
    { question: 'How is Trending calculated?', answer: 'Trending ranks publicly discoverable, ready songs by overall engagement: plays are weighted at 80 points each, while Loves, playlist saves, and shares contribute 20 points each. Newer songs break ties. The Home view displays the top three.' },
    { question: 'How does Featured work?', answer: 'Featured is automatic, not manually assigned. It uses the previous day’s engagement signals and a separate score: 60 points per play, plus 20 points per Love, save, and share. At least three engaged songs are needed for engagement-led selection; the shortlist can contain up to eight, and the highest-ranked eligible song becomes the hero.' },
    { question: 'Which songs can appear on Home?', answer: 'Only songs that are ready and explicitly public (discoverable) can appear in Featured, Trending, or Explore. Private and unlisted songs remain out of the public catalogue.' },
  ],
  'text-music': [
    { question: 'How specific should my prompt be?', answer: 'Include the subject, mood, genre, tempo, instruments, vocal direction, and language when those choices matter. A short, concrete brief usually gives more control than a paragraph of vague adjectives.' },
    { question: 'Can I change the result without starting over?', answer: 'Edit the prompt and generate again. Treat each result as a new interpretation; save the versions you want to compare in Library.' },
    { question: 'Can I write the lyrics myself?', answer: 'Yes. Include your lyrics in the prompt, preferably after a clear “Lyrics:” label, and use section labels such as [Verse], [Chorus], [Bridge], and [Outro]. You can also ask Lyrixsa to write new lyrics from a theme.' },
    { question: 'Can I give a very long or explicit instruction?', answer: 'You can provide an extended creative brief with detailed structure, timestamps, instrumentation, vocal tone, language, BPM, key, dynamics, backing vocals, sound effects, and production style. Explicit subject matter may still be blocked by safety filters, and copyrighted lyrics or requests for a specific artist’s voice are not supported.' },
    { question: 'Can I edit a generated song exactly the way I want?', answer: 'Not at the moment. Lyrixsa generation is currently a single-turn process, so you cannot directly revise an existing recording with a precise text edit and keep everything else unchanged. Studio can offer separate AI transformations and lyric tools, but exact post-generation editing is not currently available. Our generation technology is evolving, and this may improve in the future.' },
    { question: 'What can Lyrixsa understand in a prompt?', answer: 'Lyrixsa can work from text and images, generate vocals and timed lyrics, and follow directions about genre, era, mood, instruments, tempo, key, language, vocal character, song sections, energy changes, timestamps, backing vocals, ambience, and instrumental-only output. Results are creative interpretations, not guaranteed exact instructions.' },
  ],
  dna: [
    { question: 'Does Music DNA copy the reference song?', answer: 'No. It analyzes musical characteristics such as genre, mood, energy, and tempo to help you describe a new work. Do not request a reproduction of a copyrighted recording.' },
    { question: 'Why did analysis fail?', answer: 'Check that the URL is a public Spotify track link and that the track is available in your region. Try another link if Spotify cannot provide metadata for the first one.' },
  ],
  'mixer-start': [
    { question: 'How many genres do I need?', answer: 'Choose at least two genres. A third slot is optional and is useful when you want a more distinctive blend.' },
    { question: 'What does the compatibility score mean?', answer: 'It is a creative guidance signal, not a quality guarantee. A lower score can still produce an interesting result when the prompt explains how the genres should work together.' },
  ],
  mixer: [
    { question: 'Can I control the tempo and energy?', answer: 'Yes. Use the mixer controls to set mood, tempo, vocal direction, language, length, and energy before creating.' },
    { question: 'Why is my mix not available?', answer: 'Confirm that the required genre slots are selected. Missing inputs and account restrictions are shown before the request is submitted.' },
  ],
  'mixer-create': [
    { question: 'Can I edit the generated prompt?', answer: 'Yes. The generated prompt is a starting point. Rewrite sections, add constraints, or remove details before creating the song.' },
    { question: 'What does Regenerate Prompt do?', answer: 'It creates another prompt from the same mixer choices. Use it when the musical direction is right but the wording is not.' },
  ],
  'image-music': [
    { question: 'What image formats are supported?', answer: 'Use a clear PNG, JPG, WEBP, or GIF image within the upload limit shown by the interface. Avoid images you do not have permission to use.' },
    { question: 'Is the description required?', answer: 'No. It is optional, but a short description can clarify the intended scene, emotion, genre, or vocal direction when the image is ambiguous.' },
  ],
  library: [
    { question: 'Can I recover a deleted song?', answer: 'Deletion should be treated as permanent unless the interface explicitly offers recovery. Download or duplicate important work before deleting it.' },
  ],
  studio: [
    { question: 'How do I start editing a song in Studio?', answer: 'For a quick edit, open Library, click the three-dot menu on the song card, and choose Open in Studio. For heavier operations, open Studio and create a new project. Dedicated projects can always be revisited, so they are the better choice for work you expect to continue.' },
    { question: 'How does regeneration work in Studio?', answer: 'Regeneration currently creates a new interpretation with a different melody from the initial song. Because it happens inside Studio, the result is saved as a new version rather than silently replacing the original, and that new version becomes the editable HEAD. Studio operations apply to the current HEAD; switch to another version in Versions History when you want to work from a different point. Do not expect regeneration to replace only the section containing edited lyrics: it regenerates the whole song and may also produce a different genre, tempo, vocal character, or arrangement.' },
    { question: 'Which changes create a new version?', answer: 'AI edits such as Extend, Remix, Replace, stem work, and cover generation produce an updated or additional result. Review the result in Library before using it commercially.' },
    { question: 'Can I edit lyrics in Studio?', answer: 'Use the lyrics panel to inspect the active song while working with its audio and AI tools. The available actions depend on the selected tool and the song state.' },
  ],
  'vault-bonus': [
    { question: 'What is included in a Vault pack?', answer: 'Packs can contain prompts, presets, song structures, hooks, metadata guidance, and other producer resources. Open a pack to see its individual contents.' },
    { question: 'Can Vault content be used in commercial work?', answer: 'Use the pack according to the entitlement and license attached to your account. The resources are starting material; review generated output and third-party rights before release.' },
  ],
  bonus: [
    { question: 'Why can I not claim a bonus?', answer: 'Bonuses may require the eligible account access or may already be claimed. Check your plan and the claim state shown on the Bonus page.' },
    { question: 'Where does a claimed bonus go?', answer: 'After claiming, open the destination named by the bonus card, such as Library, Vault, or a creation workflow.' },
  ],
  'settings-support': [
    { question: 'Which settings affect new projects?', answer: 'Preferences can set defaults such as appearance, vocal style, export format, and email updates. Existing songs are not retroactively changed by those defaults.' },
    { question: 'How do I close my account?', answer: 'Use the Delete account action in Settings and confirm the request. Download anything you need first because account deletion may be permanent.' },
  ],
  'support-center': [
    { question: 'What should I include in a support ticket?', answer: 'Describe what you expected, what happened, the page or song involved, and any visible error. Include the account email and relevant order details for billing questions.' },
    { question: 'When should I open a ticket?', answer: 'Open a ticket for account, billing, or technical issues that require investigation and follow-up.' },
  ],
  'guide-page': [
    { question: 'Is the Full Guide different from the in-app guide?', answer: 'The in-app guide is a short reference inside the product. Full Guide is the longer documentation view with workflows and detailed answers.' },
    { question: 'How do I find a topic quickly?', answer: 'Use the search icon in the guide header, or select a section from the left navigation. Search matches section names and guide topics.' },
  ],
  'cover-art': [
    { question: 'Can I edit the generated cover image?', answer: 'Not in this version. Each cover image is generated once from the request and cannot be edited with image inpainting or a targeted image revision. Generate a new cover with a revised description if you want a different result.' },
    { question: 'How can I get a better cover result?', answer: 'Describe the subject, composition, mood, color palette, lighting, and style you want, and say what must be avoided. Review the result before applying it to the song.' },
  ],
  'stem-splitting': [
    { question: 'Can I listen to the separated stems before downloading them?', answer: 'Yes. Preview the individual stems in Studio after processing completes, then download the complete stem set as a ZIP file when you are satisfied.' },
    { question: 'What is included in the ZIP file?', answer: 'The ZIP contains the separated audio layers returned for that song. Keep the archive together until you have imported the stems into your editing or production software.' },
  ],
  'audio-inpainting': [
    { question: 'What is the difference between Replace, Remix, and Extend?', answer: 'Replace cuts out a user-selected range and generates a new section for a separately chosen duration. The range controls what is removed; Duration (seconds) controls the length of the replacement, so they do not have to match. Remix references the original audio to create a different interpretation. Extend creates new material beyond the existing audio. None of these is a direct, sample-perfect edit of the original waveform yet.' },
    { question: 'What does Steps mean in Replace?', answer: 'Steps is the number of refinement iterations used to reproduce the replacement section. A higher value generally requires more processing and takes longer to return the audio artifact; it can change the result, but it does not guarantee a more faithful reproduction.' },
    { question: 'How do Temperature and Variance work in Extend?', answer: 'Temperature controls the conservativeness of sampling: higher temperature means more diversity in the extension. Variance controls the influence of the inputs: higher values produce lower-variance output that adheres more closely to the source. You can also add optional instructions to refine the extended artifact.' },
    { question: 'How does Remix differ from Regenerate?', answer: 'Regenerate creates a whole-song alternative from the current instructions. Remix uses the loaded audio as a reference and tries to maintain its melody while creating a new artifact. Remix can also override the lyrics and accept additional instructions that influence the result.' },
    { question: 'Why did the result change more than I expected?', answer: 'The model references the original audio and recreates a new artifact, so it may introduce artifacts, timing differences, or changes outside the selected area. Preview the result carefully and keep the original version available in Versions History.' },
  ],
  'preview-mode': [
    { question: 'What does Mix do?', answer: 'Mix triggers Preview Mode. It lets you hear what you have mixed without saving or applying the result to the song yet.' },
    { question: 'Can I compare the mix with the original audio?', answer: 'Yes. You can switch between the mix preview and the loaded audio seamlessly while checking the result. The Balance section stays unavailable until the stems have been split.' },
  ],
  'audio-editing': [
    { question: 'What happens before I can edit audio?', answer: 'Lyrixsa automatically parses the loaded audio and analyzes its default data first. The editing controls become available after that preparation step completes.' },
    { question: 'Does Audio Editing regenerate the whole song?', answer: 'No. Direct editing tools operate on the parsed audio for timing and mix adjustments. AI operations such as Regenerate, Remix, Replace, and Extend are separate workflows.' },
  ],
  uploads: [
    { question: 'Does the Certificate License cover uploaded songs?', answer: 'No. Uploaded songs are not covered by the Lyrixsa Certificate License because Lyrixsa cannot verify their origin, ownership, or complete production history. They cannot be delivered through Lyrixsa Publisher.' },
    { question: 'What if I created the song on Lyrixsa and uploaded it again?', answer: 'The re-uploaded file is still treated as uncovered uploaded audio. Even if it was originally created on Lyrixsa and later downloaded, re-uploading means Lyrixsa cannot reliably recover external weights, source context, or fingerprinting information that may be relevant to the file. It cannot be delivered through Lyrixsa Publisher.' },
  ],
  'versions-history': [
    { question: 'Does editing change my original song?', answer: 'No. Studio editing is non-destructive: it creates a new edited song/version rather than altering the existing song. The new result becomes the HEAD automatically for further editing.' },
    { question: 'How do I change the version I am editing?', answer: 'Open Versions History and explicitly load the version you want. Loading it changes the HEAD, so subsequent Studio operations apply to that version.' },
    { question: 'What can I do with a version?', answer: 'You can compare versions, preview them, load one as the current HEAD, save any version as a new song in Library, or delete versions you no longer need.' },
  ],
  'publisher-release': [
    { question: 'What can I release through Publisher?', answer: 'You can prepare a Single, EP, or Album using songs already in your Lyrixsa Library. The tracks remain in Library; the release is a separate publishing record that groups and orders them for distribution.' },
    { question: 'Which release information is required?', answer: 'Complete the release title, artist name, genre, language, explicit-content choice, selected tracks, cover art, and destination outlets shown by the workflow. A release date and lyrics can be optional where the interface says so.' },
    { question: 'Can I change the track order?', answer: 'Choose tracks in the order they should appear on the release and review that order before submission. For an EP or Album, treat the final sequence as release metadata, not merely a temporary playlist.' },
    { question: 'Does Publisher change the songs in my Library?', answer: 'No. Selecting tracks or choosing release artwork does not alter the original Library songs. The Publisher creates and updates a separate release record.' },
    { question: 'Can I publish uploaded music?', answer: 'Yes, but you are responsible for having all necessary rights. An uploaded file is not covered by the Lyrixsa Certificate License because Lyrixsa cannot verify its full origin or production history, even when a downloaded Lyrixsa song is uploaded again.' },
    { question: 'Do I need a Commercial License?', answer: 'Yes. A valid Commercial License is required before music can be submitted for commercial release through Publisher.' },
    { question: 'Does submission mean the release is already live?', answer: 'No. Submission places the release into review and delivery processing. Each store or streaming service controls its own review and availability timing, so rely on the release and outlet statuses shown in Publisher rather than treating submission as a live date.' },
  ],
  'publisher-dashboard': [
    { question: 'What do the Publisher statuses mean?', answer: 'Draft means the release is still being prepared. In review and processing mean it has entered the submission workflow. Live or delivered information is shown only when a corresponding distribution status has been received. Needs Attention or Failed means you should open the release and review the recorded issue.' },
    { question: 'Can I continue an unfinished release later?', answer: 'Yes. Draft progress is retained, and returning to Publisher can reopen the most recent release surface. Use the dashboard when you want to view all releases instead.' },
    { question: 'What appears in Activity?', answer: 'Activity organizes release milestones, store updates, earnings records, and issues. Filter by event type and date range to narrow the timeline, then open an event when you need its related release.' },
    { question: 'Can I delete a release?', answer: 'Drafts and eligible release records can be removed where the Delete action is available. A submitted release may require a withdrawal or takedown process rather than ordinary deletion, because removing the Lyrixsa record alone would not recall files already delivered externally.' },
    { question: 'Why does a release need attention?', answer: 'Open the release to see the recorded reason. Common causes include missing metadata, artwork or audio problems, rights questions, or an outlet-specific response. Correct only what the notice identifies, then continue through the available review or resubmission action.' },
  ],
  'publisher-reporting': [
    { question: 'Are analytics available immediately?', answer: 'Usually not. Reporting depends on data returned after release activity has been processed, and different services report on different schedules. A blank or unavailable metric should not be interpreted as zero unless the interface explicitly reports zero.' },
    { question: 'Can every outlet report every metric?', answer: 'No. Streams, listeners, saves, playlist activity, geography, and similar fields vary by outlet and reporting period. Publisher identifies unavailable information instead of estimating it.' },
    { question: 'What is the difference between earnings and net revenue?', answer: 'Reported earnings are the gross amounts received for the selected period. The Financial section can show applicable deductions separately; the remaining amount is the reported net amount before or after any additional treatment explicitly described there.' },
    { question: 'When can I receive a payout?', answer: 'Payout availability depends on reported earnings, the configured payout method, any applicable threshold or hold, and the status shown in Financial. Keep payout details accurate in Settings and use the displayed payout status as the current source of truth.' },
    { question: 'Why might reports change later?', answer: 'Streaming and earnings information can arrive in later reporting cycles or be corrected by an outlet. Publisher displays the latest supported records available to the account, so totals for a past period can be updated.' },
  ],
  'publisher-credits-settings': [
    { question: 'Which settings should I check before releasing?', answer: 'Confirm your Publisher identity, payout method and required payout details, default genre, default release type, outlet preferences, and explicit-content preference. Defaults reduce repeated entry but should still be reviewed for every release.' },
  ],
};

const DETAILED_PROMPT = `Create a 2-minute 30-second cinematic alt-pop song in A minor at 108 BPM about leaving a familiar city to start again.

Begin with four bars of intimate felt piano and a distant vinyl texture. Bring in muted electric guitar, warm analog synth pads, a rounded bass line, and a restrained kick-and-snare groove in the first verse.

Use this structure:
[Intro] -> [Verse 1] -> [Pre-Chorus] -> [Chorus] -> [Verse 2] -> [Chorus] -> [Bridge] -> [Final Chorus] -> [Outro]

Keep Verse 1 sparse and conversational. Build tension in the pre-chorus, then make the chorus wide and hopeful with layered harmonies. Use a warm, expressive female alto with a slightly breathy tone; sing in English with clear diction and no rap.

Write original lyrics with concrete city imagery, an eight-line verse, a memorable four-line chorus, and a bridge that changes from uncertainty to resolve. Add a subtle vocal double only in the final chorus, a short melodic guitar response after each chorus line, and a gradual lift in energy from intimate to anthemic.

Avoid trap hi-hats, EDM drops, distorted vocals, and abrupt tempo changes. End with the piano motif alone and a soft sustained vocal note.`;

type GuideNavItem =
  | {
      id: string;
      title: string;
      category: string;
      kind: 'index';
    }
  | {
      id: string;
      title: string;
      category: string;
      kind: 'walkthrough';
      section: GuideSection;
    }
  | {
      id: string;
      title: string;
      category: string;
      kind: 'answer';
      answer: string;
    };

interface GuideNavGroup {
  id: string;
  title: string;
  items: GuideNavItem[];
}

const guideSectionById = new Map([...guideSections, ...studioDocSections].map((section) => [section.id, section]));
const faqTopicById = new Map(FAQ_TOPICS.map((topic) => [topic.id, topic]));

function walkthroughItems(sectionIds: string[], category: string): GuideNavItem[] {
  return sectionIds.map((sectionId) => {
    const section = guideSectionById.get(sectionId);
    if (!section) throw new Error(`Unknown guide section: ${sectionId}`);
    return {
      id: `walkthrough-${section.id}`,
      title: section.title,
      category,
      kind: 'walkthrough' as const,
      section,
    };
  });
}

function questionItems(topicId: string, category: string): GuideNavItem[] {
  const topic = faqTopicById.get(topicId);
  if (!topic) throw new Error(`Unknown FAQ topic: ${topicId}`);
  return topic.questions.map((question, index) => ({
    id: `answer-${topic.id}-${index + 1}`,
    title: question.q,
    category,
    kind: 'answer' as const,
    answer: question.a,
  }));
}

const allContentItem: GuideNavItem = {
  id: 'all-content',
  title: 'All content',
  category: 'Guide',
  kind: 'index',
};

const guideNavGroups: GuideNavGroup[] = [
  {
    id: 'start',
    title: 'Start here',
    items: [allContentItem],
  },
  {
    id: 'home',
    title: 'Home',
    items: walkthroughItems(['home'], 'Home'),
  },
  {
    id: 'create',
    title: 'Create',
    items: [
      ...walkthroughItems(
        ['text-music', 'dna', 'mixer-start', 'mixer', 'mixer-create', 'image-music'],
        'Create'
      ),
      ...questionItems('generating', 'Create'),
    ],
  },
  {
    id: 'library',
    title: 'Library',
    items: walkthroughItems(['library'], 'Library'),
  },
  {
    id: 'studio',
    title: 'Lyrixsa Studio',
    items: walkthroughItems(
      ['studio', 'stem-splitting', 'audio-inpainting', 'cover-art', 'audio-editing', 'preview-mode', 'uploads', 'versions-history'],
      'Lyrixsa Studio'
    ),
  },
  {
    id: 'publisher',
    title: 'Publisher',
    items: walkthroughItems(
      ['publisher-release', 'publisher-dashboard', 'publisher-reporting', 'publisher-credits-settings'],
      'Publisher'
    ),
  },
  {
    id: 'billing',
    title: 'Billing',
    items: [
      ...questionItems('billing', 'Billing'),
      ...questionItems('credits', 'Billing'),
      ...walkthroughItems(['vault-bonus'], 'Billing'),
    ],
  },
  {
    id: 'settings-resources',
    title: 'Settings & Resources',
    items: [
      ...walkthroughItems(
        ['settings-support', 'support-center', 'guide-page', 'bonus'],
        'Settings & Resources'
      ),
      ...questionItems('account', 'Settings & Resources'),
    ],
  },
];
const guideNavItems = guideNavGroups.flatMap((group) => group.items);

function guideItemSearchText(item: GuideNavItem): string {
  if (item.kind === 'answer') {
    return `${item.title} ${item.category} ${item.answer}${item.title === 'How do I write a good prompt?' ? ` ${DETAILED_PROMPT}` : ''}`;
  }
  if (item.kind === 'walkthrough') {
    const section = item.section;
    const questions = GUIDE_QUESTIONS[section.id] ?? [];
    return [
      item.title,
      item.category,
      section.summary,
      section.imageAlt,
      ...section.steps,
      ...section.callouts.map((callout) => callout.label),
      ...questions.flatMap((question) => [question.question, question.answer]),
    ].join(' ');
  }
  return `${item.title} ${item.category}`;
}

function guideItemPreviewText(item: GuideNavItem): string {
  if (item.kind === 'answer') return item.answer;
  if (item.kind === 'walkthrough') {
    const questions = GUIDE_QUESTIONS[item.section.id] ?? [];
    return [item.section.summary, ...item.section.steps, ...questions.map((question) => question.answer)].join(' ');
  }
  return item.title;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightSearchTerms(text: string, query: string): ReactNode {
  const terms = query.trim().split(/\s+/).filter(Boolean).map(escapeRegExp);
  if (terms.length === 0) return text;
  const matcher = new RegExp(`(${terms.join('|')})`, 'gi');
  return text.split(matcher).map((part, index) =>
    terms.some((term) => new RegExp(`^${term}$`, 'i').test(part))
      ? <mark key={`${part}-${index}`}>{part}</mark>
      : part
  );
}

function makeSearchPreview(item: GuideNavItem, query: string): string {
  const text = guideItemPreviewText(item).replace(/\s+/g, ' ').trim();
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const matchIndex = normalizedQuery ? text.toLocaleLowerCase().indexOf(normalizedQuery) : -1;
  if (matchIndex < 0 || matchIndex <= 72) return text.slice(0, 240);
  return `...${text.slice(Math.max(0, matchIndex - 72), matchIndex + 168)}`;
}

function StepCard({ children, index }: { children: string; index: number }) {
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr',
        gap: 10,
        alignItems: 'start',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: c.accentSoft,
          border: `1px solid ${c.accentBorder}`,
          color: c.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </span>
      <span style={{ color: c.text2, fontSize: 13, lineHeight: 1.55 }}>{children}</span>
    </li>
  );
}

function PromptExample() {
  const [copied, setCopied] = useState(false);

  async function copyPrompt(): Promise<void> {
    try {
      await navigator.clipboard.writeText(DETAILED_PROMPT);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="lyx-full-guide-prompt-example">
      <div className="lyx-full-guide-prompt-toolbar">
        <span>Detailed prompt example</span>
        <button type="button" onClick={() => void copyPrompt()} aria-label="Copy detailed prompt">
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre tabIndex={0}>{DETAILED_PROMPT}</pre>
    </div>
  );
}

function AnnotatedFigure({ section }: { section: GuideSection }) {
  return (
    <figure style={{ margin: 0 }}>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 10,
          border: `1px solid ${c.border}`,
          background: c.surface1,
        }}
      >
        <img
          src={section.image}
          alt={section.imageAlt}
          width={2942}
          height={1808}
          loading="lazy"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
          }}
        />
        <svg
          viewBox="0 0 100 61.45"
          aria-hidden="true"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          <defs>
            <marker
              id={`arrow-${section.id}`}
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L6,3 L0,6 Z" fill="#f2a93c" />
            </marker>
          </defs>
          {section.callouts.map((callout) => (
            <g key={callout.label}>
              <rect
                x={callout.x}
                y={callout.y}
                width={callout.w}
                height={callout.h}
                rx="1.1"
                fill="rgba(242,169,60,0.08)"
                stroke="#f2a93c"
                strokeWidth="0.35"
              />
              {callout.arrow && (
                <line
                  x1={callout.arrow.fromX}
                  y1={callout.arrow.fromY}
                  x2={callout.arrow.toX}
                  y2={callout.arrow.toY}
                  stroke="#f2a93c"
                  strokeWidth="0.42"
                  markerEnd={`url(#arrow-${section.id})`}
                />
              )}
              <circle
                cx={callout.x + callout.w / 2}
                cy={callout.y + callout.h / 2}
                r="1.65"
                fill="rgba(16,17,20,0.84)"
                stroke="#f2a93c"
                strokeWidth="0.3"
              />
            </g>
          ))}
        </svg>
      </div>
      <figcaption style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {section.callouts.map((callout, index) => (
          <span
            key={callout.label}
            style={{
              border: `1px solid ${c.border}`,
              background: c.surface1,
              color: c.text2,
              borderRadius: 999,
              padding: '5px 9px',
              fontSize: 11,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{ color: c.accent, fontWeight: 700 }}>{index + 1}</span>
            {callout.label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

export default function VisualGuide() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const initialItem = (() => {
    const hash = typeof window === 'undefined' ? '' : window.location.hash.slice(1);
    return guideNavItems.find((item) => item.id === hash) ?? guideNavItems[0];
  })();
  const initialGroup =
    guideNavGroups.find((group) => group.items.some((item) => item.id === initialItem.id)) ??
    guideNavGroups[0];
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMounted, setSearchMounted] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(initialGroup.id);
  const [activeItemId, setActiveItemId] = useState(initialItem.id);
  const contentRef = useRef<HTMLElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const activeConnectorRef = useRef<HTMLSpanElement>(null);
  const searchControlRef = useRef<HTMLDivElement>(null);
  const panelScrollPositions = useRef(new WeakMap<HTMLElement, number>());
  const headerCollapsedRef = useRef(false);
  const headerTransitionEndsAt = useRef(0);
  const activeItemIdRef = useRef(initialItem.id);
  const pendingScrollItemId = useRef<string | null>(null);
  const activeTrackingPausedUntil = useRef(0);

  const filteredGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return guideNavGroups;
    return guideNavGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.title.toLocaleLowerCase().includes(normalizedQuery) ||
            item.category.toLocaleLowerCase().includes(normalizedQuery)
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [query]);

  const searchResults = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return [];
    return guideNavItems
      .filter((item) => item.kind !== 'index')
      .filter((item) => guideItemSearchText(item).toLocaleLowerCase().includes(normalizedQuery))
      .slice(0, 12);
  }, [query]);

  useEffect(() => {
    if (searchOpen) {
      setSearchMounted(true);
      return;
    }
    const timeout = window.setTimeout(() => setSearchMounted(false), SEARCH_TRANSITION_MS);
    return () => window.clearTimeout(timeout);
  }, [searchOpen]);

  const activeGroup = guideNavGroups.find((group) => group.id === activeGroupId) ?? guideNavGroups[0];
  const headerLabelText = activeGroup.id === 'start' ? 'Guide' : activeGroup.title;
  const headerLabelAccent = activeGroup.id !== 'start';
  const previousHeaderLabel = useRef({ text: headerLabelText, accent: headerLabelAccent });
  const [outgoingHeaderLabel, setOutgoingHeaderLabel] = useState<{
    text: string;
    accent: boolean;
  } | null>(null);

  const positionActiveConnector = useCallback((itemId: string): void => {
    const sidebar = sidebarRef.current;
    const connector = activeConnectorRef.current;
    const item = sidebar?.querySelector<HTMLElement>(`[data-guide-nav-item-id="${itemId}"]`);
    if (!sidebar || !connector || !item) {
      connector?.classList.remove('is-visible');
      return;
    }

    const sidebarRect = sidebar.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const visibleControls = Array.from(
      sidebar.querySelectorAll<HTMLElement>(
        '.lyx-full-guide-nav-heading, [data-guide-nav-item-id], .lyx-full-guide-nav-support-link'
      )
    ).filter((control) => control.getClientRects().length > 0);
    const nextControl = visibleControls.find(
      (control) => control !== item && control.getBoundingClientRect().top >= itemRect.bottom
    );
    const nextTop = nextControl?.getBoundingClientRect().top ?? itemRect.bottom + 24;
    const y = (itemRect.bottom + nextTop) / 2 - sidebarRect.top;
    connector.style.setProperty('--lyx-guide-connector-y', `${y}px`);
    connector.classList.add('is-visible');
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Lyrixsa AI Full Guide';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    const previous = previousHeaderLabel.current;
    if (previous.text === headerLabelText && previous.accent === headerLabelAccent) return;

    setOutgoingHeaderLabel(previous);
    previousHeaderLabel.current = { text: headerLabelText, accent: headerLabelAccent };
    const timeout = window.setTimeout(() => setOutgoingHeaderLabel(null), 260);
    return () => window.clearTimeout(timeout);
  }, [headerLabelAccent, headerLabelText]);

  useEffect(() => {
    if (!searchOpen) return;

    function closeSearch(event: PointerEvent): void {
      const target = event.target as Node;
      if (!searchControlRef.current?.contains(target) && !(target as Element).closest?.('.lyx-full-guide-search-overlay')) {
        setSearchOpen(false);
      }
    }
    function closeSearchWithEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') setSearchOpen(false);
    }

    document.addEventListener('pointerdown', closeSearch);
    document.addEventListener('keydown', closeSearchWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeSearch);
      document.removeEventListener('keydown', closeSearchWithEscape);
    };
  }, [searchOpen]);

  useLayoutEffect(() => {
    positionActiveConnector(activeItemId);
  }, [activeGroupId, activeItemId, positionActiveConnector, query]);

  useEffect(() => {
    const itemId = pendingScrollItemId.current;
    if (!itemId || !contentRef.current) return;
    pendingScrollItemId.current = null;

    const panel = contentRef.current;
    const target = panel.querySelector<HTMLElement>(`[data-guide-item-id="${itemId}"]`);
    if (!target) return;
    const top = target.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop - 24;
    if (typeof panel.scrollTo === 'function') {
      panel.scrollTo({ top, behavior: 'auto' });
    } else {
      panel.scrollTop = top;
    }
  }, [activeGroupId, activeItemId]);

  function selectItem(item: GuideNavItem): void {
    const group = guideNavGroups.find((candidate) =>
      candidate.items.some((groupItem) => groupItem.id === item.id)
    );
    if (!group) return;

    pendingScrollItemId.current = item.kind === 'index' ? null : item.id;
    activeTrackingPausedUntil.current = Date.now() + SUBNAV_JUMP_GUARD_MS;
    activeItemIdRef.current = item.id;
    setActiveGroupId(group.id);
    setActiveItemId(item.id);
    updateHeader(false, true);
    contentRef.current?.focus({ preventScroll: true });
    if (item.kind === 'index' && contentRef.current) contentRef.current.scrollTop = 0;
    window.history.replaceState(null, '', `${window.location.pathname}#${item.id}`);
  }

  function selectGroup(group: GuideNavGroup): void {
    const firstItem = group.items[0];
    pendingScrollItemId.current = null;
    activeTrackingPausedUntil.current = Date.now() + SUBNAV_JUMP_GUARD_MS;
    setActiveGroupId(group.id);
    activeItemIdRef.current = firstItem?.id ?? '';
    setActiveItemId(firstItem?.id ?? '');
    updateHeader(false, true);
    contentRef.current?.focus({ preventScroll: true });
    if (contentRef.current) contentRef.current.scrollTop = 0;
    window.history.replaceState(null, '', `${window.location.pathname}#section-${group.id}`);
  }

  function updateHeader(collapsed: boolean, guardUnchanged = false): void {
    if (headerCollapsedRef.current === collapsed) {
      if (guardUnchanged) {
        headerTransitionEndsAt.current = Date.now() + HEADER_TRANSITION_GUARD_MS;
      }
      return;
    }
    if (collapsed) setSearchOpen(false);
    headerCollapsedRef.current = collapsed;
    headerTransitionEndsAt.current = Date.now() + HEADER_TRANSITION_GUARD_MS;
    setHeaderCollapsed(collapsed);
  }

  function handlePanelScroll(event: UIEvent<HTMLElement>): void {
    const panel = event.currentTarget;
    const currentScrollTop = panel.scrollTop;
    const previousScrollTop = panelScrollPositions.current.get(panel) ?? 0;
    panelScrollPositions.current.set(panel, currentScrollTop);

    if (panel === contentRef.current && Date.now() >= activeTrackingPausedUntil.current) {
      updateActiveItemFromScroll(panel);
    }
    if (panel !== contentRef.current) positionActiveConnector(activeItemIdRef.current);

    // Resizing the panes while the header animates also emits scroll events. Those
    // events describe layout movement, not user intent, and must not reverse the header.
    if (Date.now() < headerTransitionEndsAt.current) return;

    if (currentScrollTop <= 8) {
      updateHeader(false);
    } else if (currentScrollTop > previousScrollTop + 2) {
      updateHeader(true);
    } else if (currentScrollTop < previousScrollTop - 2) {
      updateHeader(false);
    }
  }

  function updateActiveItemFromScroll(panel: HTMLElement): void {
    const sections = Array.from(panel.querySelectorAll<HTMLElement>('[data-guide-item-id]'));
    if (sections.length === 0) return;

    const panelTop = panel.getBoundingClientRect().top;
    const readingLine = panelTop + Math.min(160, panel.clientHeight * 0.25);
    let current = sections[0];
    for (const section of sections) {
      if (section.getBoundingClientRect().top <= readingLine) current = section;
    }
    if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 4) {
      current = sections[sections.length - 1];
    }

    const itemId = current.dataset.guideItemId;
    if (!itemId || itemId === activeItemIdRef.current) return;
    activeItemIdRef.current = itemId;
    setActiveItemId(itemId);
    window.history.replaceState(null, '', `${window.location.pathname}#${itemId}`);
  }

  return (
    <div className={`lyx-full-guide-page${headerCollapsed ? ' is-header-collapsed' : ''}`}>
      <header className="lyx-full-guide-header" aria-hidden={headerCollapsed}>
        <div className="lyx-full-guide-header-inner">
          <Link
            to={isAuthenticated ? paths.dashboard : paths.login}
            aria-label={isAuthenticated ? 'Lyrixsa AI dashboard' : 'Lyrixsa AI login'}
            className="lyx-full-guide-brand"
          >
            <span className="lyx-full-guide-brand-identity">
              <Logo size={30} />
            </span>
            <span className="lyx-full-guide-brand-divider" aria-hidden="true" />
            <span className="lyx-full-guide-brand-label-stage" aria-live="polite">
              {outgoingHeaderLabel && (
                <span
                  className={`lyx-full-guide-brand-label is-outgoing${outgoingHeaderLabel.accent ? ' is-section' : ''}`}
                  aria-hidden="true"
                >
                  {outgoingHeaderLabel.text}
                </span>
              )}
              <span
                key={activeGroup.id}
                className={`lyx-full-guide-brand-label is-current${headerLabelAccent ? ' is-section' : ''}`}
              >
                {headerLabelText}
              </span>
            </span>
          </Link>
          <div ref={searchControlRef} className="lyx-full-guide-header-search">
            <button
              type="button"
              className={`lyx-full-guide-search-button${query ? ' has-query' : ''}`}
              aria-label="Search the guide"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((open) => !open)}
            >
              <Search size={18} strokeWidth={1} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {searchMounted && (
        <div className={`lyx-full-guide-search-overlay${searchOpen ? '' : ' is-closing'}`} role="dialog" aria-label="Search the guide" onMouseDown={() => setSearchOpen(false)}>
          <div className="lyx-full-guide-search-panel" onMouseDown={(event) => event.stopPropagation()}>
            <input
              type="search"
              aria-label="Search the guide"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
            {query.trim() && (
              <div className="lyx-full-guide-search-results" aria-live="polite">
                {searchResults.length > 0 ? searchResults.map((item) => (
                  <button key={item.id} type="button" onClick={() => { selectItem(item); setQuery(''); setSearchOpen(false); }}>
                    <span className="lyx-full-guide-search-result-copy">
                      <strong>{item.title}</strong>
                      <span>{highlightSearchTerms(makeSearchPreview(item, query), query)}</span>
                    </span>
                    <small>{item.category}</small>
                  </button>
                )) : <p>No guide content matches your search.</p>}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="lyx-full-guide-shell">
        <aside
          ref={sidebarRef}
          className="lyx-full-guide-sidebar"
          aria-label="Guide navigation"
        >
          <nav className="lyx-full-guide-sidebar-nav" aria-label="Guide topics">
            <div className="lyx-full-guide-sidebar-inner" onScroll={handlePanelScroll}>
              <div className="lyx-full-guide-nav">
              {filteredGroups.map((group) => (
                <section key={group.id} className="lyx-full-guide-nav-group">
                  <h2>
                    <button
                      type="button"
                      className={`lyx-full-guide-nav-heading${group.id === activeGroup.id ? ' is-active' : ''}`}
                      aria-expanded={group.id === activeGroup.id}
                      aria-controls={`full-guide-nav-${group.id}`}
                      onClick={() => selectGroup(group)}
                    >
                      <span>{group.title}</span>
                      <ChevronRight
                        className="lyx-full-guide-nav-heading-chevron"
                        size={17}
                        aria-hidden="true"
                      />
                    </button>
                  </h2>
                  {group.id === activeGroup.id && (
                    <ul id={`full-guide-nav-${group.id}`}>
                      {group.items.map((item) => {
                        const active = item.id === activeItemId;
                        return (
                          <li key={item.id}>
                            <button
                              type="button"
                              className={active ? 'is-active' : undefined}
                              aria-current={active ? 'page' : undefined}
                              data-guide-nav-item-id={item.id}
                              onClick={() => selectItem(item)}
                            >
                              <span>{item.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              ))}
              {filteredGroups.length === 0 && (
                <p className="lyx-full-guide-empty">No guide topics match your search.</p>
              )}
              </div>
            </div>
            <Link to={paths.support} className="lyx-full-guide-nav-support-link">
              Support Center
            </Link>
          </nav>
          <span
            ref={activeConnectorRef}
            className="lyx-full-guide-active-connector"
            aria-hidden="true"
          />
        </aside>

        <article
          ref={contentRef}
          className="lyx-full-guide-content"
          aria-labelledby="full-guide-article-title"
          tabIndex={-1}
          onPointerDown={() => contentRef.current?.focus({ preventScroll: true })}
          onScroll={handlePanelScroll}
        >
          {activeGroup.id === 'start' ? (
            <header className="lyx-full-guide-article-header">
              <p>Guide</p>
              <h1 id="full-guide-article-title">{allContentItem.title}</h1>
              <span>Every walkthrough and frequently asked question in the Lyrixsa AI guide.</span>
            </header>
          ) : (
            <h1 id="full-guide-article-title" className="lyx-visually-hidden">
              {activeGroup.title}
            </h1>
          )}

          {activeGroup.id === 'start' ? (
            <div className="lyx-full-guide-index">
              {guideNavGroups.map((group) => {
                const items = group.items.filter((item) => item.kind !== 'index');
                if (items.length === 0) return null;
                return (
                  <section key={group.id}>
                    <h2>{group.title}</h2>
                    <ul>
                      {items.map((item) => (
                        <li key={item.id}>
                          <button type="button" onClick={() => selectItem(item)}>
                            <span>{item.title}</span>
                            <ChevronRight size={15} aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          ) : activeGroup.items.length > 0 ? (
            <div className="lyx-full-guide-section-body">
              {activeGroup.items.map((item) => (
                <section
                  key={item.id}
                  className="lyx-full-guide-body-item"
                  data-guide-item-id={item.id}
                  aria-labelledby={`${item.id}-title`}
                >
                  <header className="lyx-full-guide-body-item-header">
                    <h2 id={`${item.id}-title`}>{item.title}</h2>
                    {item.kind === 'walkthrough' && <p>{item.section.summary}</p>}
                  </header>

                  {item.kind === 'walkthrough' ? (
                    <div className="lyx-full-guide-walkthrough">
                      {item.section.id === 'audio-inpainting' ? (
                        <section className="lyx-full-guide-doc-section">
                          <SectionHeading
                            as="h3"
                            size="sub"
                            title="What to expect"
                            icon={item.section.icon}
                            marginBottom={14}
                          />
                          <p className="lyx-full-guide-doc-note">
                            Audio Inpainting creates a new audio artifact from a loaded song. Replace works on a selected range, Remix references the song, and Extend adds new material. Preview every result carefully because precise, flawless editing is not yet available.
                          </p>
                        </section>
                      ) : (
                        <section className="lyx-full-guide-doc-section">
                          <SectionHeading
                            as="h3"
                            size="sub"
                            title="Steps"
                            icon={item.section.icon}
                            marginBottom={14}
                          />
                          <ol className="lyx-full-guide-steps">
                            {item.section.steps.map((step, index) => (
                              <StepCard key={step} index={index}>
                                {step}
                              </StepCard>
                            ))}
                          </ol>
                        </section>
                      )}
                      {(GUIDE_QUESTIONS[item.section.id] ?? []).length > 0 && (
                        <section className="lyx-full-guide-doc-section">
                          <SectionHeading
                            as="h3"
                            size="sub"
                            title="Questions"
                            icon={<HelpCircle size={17} color={c.accent} />}
                            marginBottom={14}
                          />
                          <div className="lyx-full-guide-doc-questions">
                            {GUIDE_QUESTIONS[item.section.id].map((entry) => (
                              <details key={entry.question}>
                                <summary>{entry.question}</summary>
                                <p>{entry.answer}</p>
                              </details>
                            ))}
                          </div>
                        </section>
                      )}
                    </div>
                  ) : item.kind === 'answer' ? (
                    <div className="lyx-full-guide-answer" aria-label="Answer">
                      <HelpCircle size={20} color={c.accent} aria-hidden="true" />
                      <div>
                        <p>{item.answer}</p>
                        {item.title === 'How do I write a good prompt?' && <PromptExample />}
                      </div>
                    </div>
                  ) : null}
                </section>
              ))}
            </div>
          ) : (
            <div className="lyx-full-guide-empty-section">Nothing to see here at the moment.</div>
          )}

          <footer className="lyx-full-guide-article-footer">
            <span>Need more help?</span>
            <Link to={paths.support}>
              Visit Support Center
              <ChevronRight size={14} aria-hidden="true" />
            </Link>
          </footer>
        </article>
      </main>
    </div>
  );
}
